import { Link } from 'react-router-dom';
import { FileText, MoreVertical, Pencil, Copy, Archive, Trash2, Share2 } from 'lucide-react';
import { Card, CardContent } from '~/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Button } from '~/components/ui/button';
import type { IFormWithCount } from '~/types';

interface IFormCardProps {
  form: IFormWithCount;
  onShare?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
}

function hashColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 50%)`;
}

export const FormCard = ({ form, onShare, onDuplicate, onArchive, onDelete }: IFormCardProps) => {
  const bgColor = form.json_schema?.theme?.primaryColor || hashColor(form.id);

  return (
    <Card className="overflow-hidden group">
      {/* Color thumbnail */}
      <Link to={`/forms/${form.id}`}>
        <div className="h-32 flex items-end p-4" style={{ backgroundColor: bgColor }}>
          <h3 className="text-white font-semibold text-lg drop-shadow-sm line-clamp-2">{form.name}</h3>
        </div>
      </Link>

      {/* Card footer */}
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <Link to={`/forms/${form.id}`} className="text-sm font-medium hover:underline truncate block">
                {form.name}
              </Link>
              <p className="text-xs text-muted-foreground">
                {form.submissionCount} response{form.submissionCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onShare && (
                <DropdownMenuItem onClick={() => onShare(form.id)}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to={`/forms/${form.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              </DropdownMenuItem>
              {onDuplicate && (
                <DropdownMenuItem onClick={() => onDuplicate(form.id)}>
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {onArchive && (
                <DropdownMenuItem onClick={() => onArchive(form.id)}>
                  <Archive className="mr-2 h-4 w-4" />
                  Archive
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem className="text-destructive" onClick={() => onDelete(form.id)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};
